import { pushRoute } from '../../../lib/router.js';
import { omit } from 'lodash';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Col, Grid, Row } from 'react-bootstrap';
import { Form } from 'react-final-form';
import {
  deleteStakeholder,
  finishUpdateStakeholderDraft,
  selectStakeholder,
  selectStakeholderDraft,
  selectStakeholderDraftId,
} from '../../../modules/stakeholders.js';
import StepCommunicatorsForm from '../../../components/projects/Stakeholders/StakeholderForms/StepCommunicatorsForm.jsx';
import EmployeeFormContainer from '../../employees/EmployeeFormContainer.jsx';
import {
  navigateToStepGroupParticipants,
  onReset,
  stakeholdersUrl,
  stakeholderUrl,
} from './utils/index.js';
import { selectProject } from '../../../modules/projects.js';
import {
  getEditorSaveDataPromise,
  getReadyStatusPromise,
} from '../../../components/editor/defaultOptions.js';
import { getOrgSlug, mapFinalFormErrors } from '../../../lib/utils.js';
import MultistepCombinedHeader from '../../../components/headers/FormHeaders/MultistepCombinedHeader.jsx';
import StakeholderFormHeader from '../../../components/headers/PageHeaders/Project/Stakeholders/StakeholderFormHeader.jsx';

const mapErrors = mapFinalFormErrors('Failed to save stakeholder');

const mapStateToProps = (state, ownProps) => ({
  stakeholderId: ownProps.stakeholderId,
  editing: !!ownProps.stakeholderId,
  projectId: ownProps.projectId,
  project: selectProject(ownProps.projectId)(state),
  draftId: selectStakeholderDraftId(state),
  stakeholder: omit(selectStakeholder(state, ownProps), [
    'id',
    'influencePosition',
    'interestPosition',
  ]),
  stakeholderDraft: selectStakeholderDraft(selectStakeholderDraftId(state))(
    state
  ),
});

const mapDispatchToProps = {
  deleteStakeholder,
  finishUpdateStakeholderDraft,
};

function StepCommunicatorsFormContainer({
  projectId,
  draftId,
  editing,
  deleteStakeholder,
  stakeholder,
  stakeholderId,
  finishUpdateStakeholderDraft,
  stakeholderDraft,
  project,
}) {
  const [selectedTrustedCommunicators, updateTrustedCommunicators] = useState(
    []
  );
  const [commentEditorInstance, setCommentEditorInstance] = useState(null);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);

  const onHideEmployeeModal = () => setShowEmployeeModal(false);
  const onShowEmployeeModal = () => setShowEmployeeModal(true);

  const goBack = () =>
    navigateToStepGroupParticipants(
      editing,
      projectId,
      stakeholderId,
      getOrgSlug()
    );

  const onSelectEmployee = (employee) => {
    const isSelected = selectedTrustedCommunicators.some(
      (e) => e.id === employee.id
    );

    if (isSelected) {
      if (!window.confirm('Are you sure you want to detach this employee?'))
        return;
      updateTrustedCommunicators(
        selectedTrustedCommunicators.filter((item) => item.id !== employee.id)
      );
    } else {
      updateTrustedCommunicators([...selectedTrustedCommunicators, employee]);
    }
  };

  const onAddEmployeeSubmitSuccess = ({ entities, result }) => {
    onSelectEmployee(entities.employees[result]);
  };

  const onSubmit = () => {
    const composedData = (editorData) => ({
      employee_ids: selectedTrustedCommunicators.map((c) => c.id),
      changes_comment: editorData.blocks.length
        ? JSON.stringify(editorData)
        : null,
    });

    return getReadyStatusPromise(commentEditorInstance).then(
      getEditorSaveDataPromise(commentEditorInstance).then((editorData) =>
        finishUpdateStakeholderDraft(draftId, composedData(editorData))
          .then(({ result }) =>
            pushRoute(stakeholderUrl, { projectId, stakeholderId: result })
          )
          .catch(mapErrors)
      )
    );
  };

  const onCancel = () => {
    if (draftId) {
      deleteStakeholder(draftId).then(() =>
        pushRoute(stakeholdersUrl, { projectId })
      );
    } else {
      pushRoute(stakeholdersUrl, { projectId });
    }
  };

  const onDelete = () => {
    if (
      !window.confirm(
        'Are you sure you want to delete this stakeholder profile?'
      )
    )
      return;
    deleteStakeholder(stakeholderId).then(() =>
      pushRoute(stakeholdersUrl, { projectId })
    );
  };

  useEffect(() => {
    !draftId && onReset(editing, projectId, stakeholderId);
  }, []);

  useEffect(() => {
    stakeholderDraft && updateTrustedCommunicators(stakeholderDraft.employees);
  }, []);

  return (
    <React.Fragment>
      <MultistepCombinedHeader
        onDelete={onDelete}
        editing={editing}
        subject="Stakeholder Profile"
        typeStep="Communicators"
      >
        {project && (editing ? Object.keys(stakeholder).length : true) ? (
          <StakeholderFormHeader
            project={project}
            editing={editing}
            stakeholder={stakeholder}
            stakeholderId={stakeholderId}
          />
        ) : null}
      </MultistepCombinedHeader>
      <Grid>
        <Row>
          <Col sm={12}>
            <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
              {draftId ? (
                <Row>
                  <Col sm={10} smOffset={1}>
                    <Form
                      editing={editing}
                      selectedTrustedCommunicators={
                        selectedTrustedCommunicators
                      }
                      onSelectEmployee={onSelectEmployee}
                      setCommentEditorInstance={setCommentEditorInstance}
                      addVersionComment={stakeholderDraft.show_comment}
                      onShowEmployeeModal={onShowEmployeeModal}
                      onSubmit={onSubmit}
                      onCancel={onCancel}
                      onBack={goBack}
                      component={StepCommunicatorsForm}
                    />
                  </Col>
                </Row>
              ) : null}
            </div>
          </Col>
        </Row>
      </Grid>
      <EmployeeFormContainer
        showFormModal={showEmployeeModal}
        onHideFormModal={onHideEmployeeModal}
        onSubmitSuccess={onAddEmployeeSubmitSuccess}
      />
    </React.Fragment>
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(StepCommunicatorsFormContainer);
